import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tooltip,
} from "antd";
import axios from "axios";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { RangePicker } = DatePicker;

const ApproveTransaction = ({ onApproval }) => {
  const getCurrentAcademicYear = () => {
    const today = new Date();
    const curYear = today.getFullYear();
    const curMonth = today.getMonth() + 1;
    const startYear = curMonth >= 6 ? curYear : curYear - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    return `${startYear}-${endYearShort}`;
  };

  const academicYears = [getCurrentAcademicYear()];
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { Option } = Select;

  const [selectedYear, setSelectedYear] = useState(getCurrentAcademicYear());
  const [classId, setClassId] = useState();
  const [studentSearch, setStudentSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [bulkRejectLoading, setBulkRejectLoading] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [receiptModal, setReceiptModal] = useState({ visible: false, record: null });
  const [receiptGenerating, setReceiptGenerating] = useState(false);

  const canApprove = user?.admin?.can_approve_transaction;

  const getTransactionBreakdown = (record) => {
    const feeCategory = record?.feeCategory;
    const type = record?.feeTemplate?.type;
    const isTuitionTemplate = type?.toLowerCase() === "tution";
    const installmentList = record?.installment_list || [];

    // Find the "other fee" (misc) installment from the live student_fee data
    const otherFeeInst = installmentList.find(
      (inst) => inst.name?.toLowerCase() === "other fee"
    );
    const miscPC = otherFeeInst
      ? (Array.isArray(otherFeeInst.payable_components)
          ? otherFeeInst.payable_components[0]
          : (otherFeeInst.payable_components || {}))
      : {};
    const otherTotal = Number(miscPC?.total_amount || 0);
    const otherPaid = Number(otherFeeInst?.paid || 0);
    const otherRemaining = Number(otherFeeInst?.remaining || 0);

    // Misc transaction — use the "other fee" installment values directly
    if (feeCategory === "other") {
      return { total: otherTotal, paid: otherPaid, remaining: otherRemaining };
    }

    // Tuition (or tuition-template) transaction
    if (isTuitionTemplate) {
      // Gross total from installment payable_components — never modified by approveDiscount
      const tuitionInsts = installmentList.filter(
        (inst) => inst.name?.toLowerCase() !== "other fee"
      );
      const grossTuition = tuitionInsts.reduce((sum, inst) => {
        const pc = Array.isArray(inst.payable_components)
          ? inst.payable_components[0]
          : (inst.payable_components || {});
        return sum + Number(pc?.total_amount || 0);
      }, 0);

      // receipt_data.displayPaid/displayPending are per-category (tuition only) — use if valid
      const rd = record?.receipt_data;
      if (rd && rd.displayTotalFee > 0) {
        const rdPaid = rd.displayPaid != null ? rd.displayPaid : 0;
        const rdRemaining = rd.displayPending || 0;
        return { total: grossTuition || rd.displayTotalFee, paid: rdPaid, remaining: rdRemaining };
      }

      // Fallback: subtract misc from top-level totals to get tuition-only paid/remaining
      const tuitionPaid = Math.max(0, (record?.total_paid || 0) - otherPaid);
      const tuitionRemaining = Math.max(0, (record?.total_remaining || 0) - otherRemaining);
      return { total: grossTuition || (record?.total_fee || 0), paid: tuitionPaid, remaining: tuitionRemaining };
    }

    // Non-tuition fee types (transport, accommodation, etc.)
    return {
      total: record?.total_fee || 0,
      paid: record?.total_paid || 0,
      remaining: record?.total_remaining || 0,
    };
  };

  // ── API helpers ──────────────────────────────────────────────────────────────

  const approveOne = (record) =>
    axios.patch(
      `${serverUrl}/admin/student-fee/${record?.feeTemplate?._id}/transaction/${record?._id}`,
      {},
      { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
    );

  const rejectOne = (record) =>
    axios.patch(
      `${serverUrl}/admin/student-fee/${record?.feeTemplate?._id}/transaction/${record?._id}/reject`,
      {},
      { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
    );

  const refresh = () => {
    queryClient.invalidateQueries(["student-fee", selectedYear, classId]);
    onApproval?.();
  };

  // ── Receipt helpers ──────────────────────────────────────────────────────────

  const openReceiptModal = (record) => {
    setReceiptModal({ visible: true, record });
  };

  const handleGenerateReceipt = async () => {
    const record = receiptModal.record;
    if (!record) return;
    setReceiptGenerating(true);
    try {
      await axios.patch(
        `${serverUrl}/admin/student-fee/${record?.feeTemplate?._id}/transaction/${record?._id}/generate-receipt`,
        {},
        { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
      );
      message.success("Receipt generated successfully");
      setReceiptModal({ visible: false, record: null });
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.error?.errors || "Failed to generate receipt");
    } finally {
      setReceiptGenerating(false);
    }
  };

  const fetchReceiptBlob = (record) =>
    axios({
      url: `${serverUrl}/admin/student-fee/${record?.feeTemplate?._id}/transaction/${record?._id}/download`,
      method: "GET",
      responseType: "blob",
      headers: {
        Accept: "application/pdf",
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

  const handleReceiptDownload = async (record) => {
    try {
      const response = await fetchReceiptBlob(record);
      const txId = record?.unique_id || record?._id || "TX";
      const s = record?.student;
      const safeName = s
        ? `${s.first_name || ""}${s.last_name ? "_" + s.last_name : ""}`.replace(/[^a-zA-Z0-9_]/g, "")
        : "Student";
      const dateStr = moment(record?.date).format("DDMMMYYYY");
      saveAs(response.data, `Receipt_${txId}_${safeName}_${dateStr}.pdf`);
    } catch (err) {
      message.error("Failed to download receipt");
    }
  };

  const handleReceiptPrint = async (record) => {
    try {
      const response = await fetchReceiptBlob(record);
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const printWindow = window.open(blobUrl, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          try { printWindow.print(); } catch (_) {}
          URL.revokeObjectURL(blobUrl);
        });
      }
    } catch (err) {
      message.error("Failed to print receipt");
    }
  };

  // ── Individual actions ───────────────────────────────────────────────────────

  const handleApprove = async (record) => {
    try {
      await approveOne(record);
      message.success("Transaction approved");
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.error?.errors || "Approval failed");
    }
  };

  const handleReject = async (record) => {
    try {
      await rejectOne(record);
      message.success("Transaction rejected");
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.error?.errors || "Rejection failed");
    }
  };

  // ── Bulk actions ─────────────────────────────────────────────────────────────

  const handleBulkApprove = async () => {
    setBulkApproveLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const key of selectedRowKeys) {
      const record = tableData.find((r) => r._id === key);
      if (record?.status === "pending") {
        try { await approveOne(record); successCount++; } catch (_) { failCount++; }
      }
    }
    setBulkApproveLoading(false);
    setSelectedRowKeys([]);
    refresh();
    if (successCount > 0 && failCount === 0) {
      message.success(`${successCount} transaction(s) approved`);
    } else if (successCount > 0 && failCount > 0) {
      message.warning(`${successCount} approved, ${failCount} failed`);
    } else if (failCount > 0) {
      message.error(`All ${failCount} approval(s) failed`);
    }
  };

  const handleBulkReject = async () => {
    setBulkRejectLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const key of selectedRowKeys) {
      const record = tableData.find((r) => r._id === key);
      if (record?.status === "pending") {
        try { await rejectOne(record); successCount++; } catch (_) { failCount++; }
      }
    }
    setBulkRejectLoading(false);
    setSelectedRowKeys([]);
    refresh();
    if (successCount > 0 && failCount === 0) {
      message.success(`${successCount} transaction(s) rejected`);
    } else if (successCount > 0 && failCount > 0) {
      message.warning(`${successCount} rejected, ${failCount} failed`);
    } else if (failCount > 0) {
      message.error(`All ${failCount} rejection(s) failed`);
    }
  };

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes",
      headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
    });
    return res.data;
  };
  const classes = useQuery("classes", fetchClasses);

  const fetchTransactions = async (selectedYear, classId) => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/admin/student-fee/transactions",
      params: { year: selectedYear, ...(classId && { classId }) },
      headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
    });
    return res.data;
  };
  const transactionApproveData = useQuery(
    ["student-fee", selectedYear, classId],
    () => fetchTransactions(selectedYear, classId)
  );

  // ── Client-side filtering ────────────────────────────────────────────────────

  const tableData = (() => {
    let data = transactionApproveData?.data?.result || [];

    if (studentSearch.trim()) {
      const q = studentSearch.trim().toLowerCase();
      data = data.filter((r) => {
        const s = r?.student;
        const name = s
          ? `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase()
          : (r?.name || "").toLowerCase();
        return name.includes(q);
      });
    }

    if (statusFilter !== "all") {
      data = data.filter((r) => r?.status === statusFilter);
    }

    if (dateRange[0] && dateRange[1]) {
      data = data.filter((r) => {
        const d = moment(r?.approvedAt || r?.date);
        return (
          d.isSameOrAfter(dateRange[0], "day") &&
          d.isSameOrBefore(dateRange[1], "day")
        );
      });
    }

    data = [...data].sort((a, b) => {
      const statusOrder = (s) => (s === "pending" ? 0 : 1);
      const statusDiff = statusOrder(a?.status) - statusOrder(b?.status);
      if (statusDiff !== 0) return statusDiff;
      if (a?.status === "pending") {
        return (
          moment(a?.createdAt || a?.date).valueOf() -
          moment(b?.createdAt || b?.date).valueOf()
        );
      }
      return (
        moment(b?.createdAt || b?.date).valueOf() -
        moment(a?.createdAt || a?.date).valueOf()
      );
    });

    return data;
  })();

  const pendingSelected = selectedRowKeys.filter((key) => {
    const r = tableData.find((t) => t._id === key);
    return r?.status === "pending";
  });

  // ── Summary cards (all statuses, filtered by date/class/year/student) ─────────
  const summaryData = (() => {
    let data = transactionApproveData?.data?.result || [];
    if (studentSearch.trim()) {
      const q = studentSearch.trim().toLowerCase();
      data = data.filter((r) => {
        const s = r?.student;
        const name = s
          ? `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase()
          : (r?.name || "").toLowerCase();
        return name.includes(q);
      });
    }
    if (dateRange[0] && dateRange[1]) {
      data = data.filter((r) => {
        const d = moment(r?.approvedAt || r?.date);
        return d.isSameOrAfter(dateRange[0], "day") && d.isSameOrBefore(dateRange[1], "day");
      });
    }
    return data;
  })();

  const approvedTx   = summaryData.filter((r) => r?.status === "approved");
  const pendingTx    = summaryData.filter((r) => r?.status === "pending");

  const sum = (arr) => arr.reduce((s, r) => s + (r?.amount || 0), 0);
  const fmt = (n) => `₹ ${Number(n).toLocaleString("en-IN")}`;

  const dateRangeLabel = dateRange[0] && dateRange[1]
    ? `${moment(dateRange[0]).format("DD MMM")} – ${moment(dateRange[1]).format("DD MMM YYYY")}`
    : "All dates";

  // ── Columns ──────────────────────────────────────────────────────────────────

  const getFeeTypeLabel = (record) => {
    const type = record?.feeTemplate?.type;
    if (type?.toLowerCase() === "tution") {
      return record?.feeCategory === "other" ? "Misc" : "Tuition";
    }
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : "--";
  };

  const statusTag = (status) => {
    const cfg = {
      pending:  { bg: "#fff7e6", color: "#d46b08", border: "#ffd591", label: "Pending" },
      approved: { bg: "#f6ffed", color: "#389e0d", border: "#b7eb8f", label: "Approved" },
      rejected: { bg: "#fff1f0", color: "#cf1322", border: "#ffa39e", label: "Rejected" },
    };
    const c = cfg[status] || { bg: "#fafafa", color: "#595959", border: "#d9d9d9", label: status || "--" };
    return (
      <span style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.03em",
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
      }}>
        {c.label}
      </span>
    );
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#8c8c8c",
    marginBottom: 6,
    display: "block",
  };

  const columns = [
    {
      title: "Txn ID",
      align: "center",
      width: 120,
      render: (r) => (
        <span style={{ fontFamily: "monospace", fontSize: 13, color: "#595959", letterSpacing: "0.04em" }}>
          {r?.unique_id || "--"}
        </span>
      ),
    },
    {
      title: "Student",
      align: "left",
      render: (r) => {
        const s = r?.student;
        const name = s ? `${s.first_name || ""} ${s.last_name || ""}`.trim() : r?.name || "--";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "linear-gradient(135deg, #1890ff22, #1890ff44)",
              color: "#1890ff", fontSize: 12, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {initials}
            </div> */}
            <span style={{ fontWeight: 600, color: "#262626", textTransform: "capitalize" }}>
              {name}
            </span>
          </div>
        );
      },
    },
    {
      title: "Class",
      align: "center",
      width: 75,
      render: (r) => (
        <span style={{ fontWeight: 500, color: "#434343" }}>{r?.class?.name || "--"}</span>
      ),
    },
    {
      title: "Fee Type",
      align: "center",
      width: 115,
      render: (r) => (
        <span style={{
          display: "inline-block", padding: "2px 10px", borderRadius: 12,
          background: "#f0f5ff", color: "#2f54eb", fontSize: 12, fontWeight: 500,
        }}>
          {getFeeTypeLabel(r)}
        </span>
      ),
    },
    {
      title: "Amount",
      align: "right",
      width: 105,
      render: (r) => (
        <span style={{ fontWeight: 700, color: "#237804", fontSize: 14 }}>
          ₹ {r?.amount?.toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Mode",
      align: "center",
      width: 85,
      render: (r) => (
        <span style={{ textTransform: "capitalize", color: "#595959", fontSize: 13 }}>
          {r?.type || "--"}
        </span>
      ),
    },
    {
      title: "Paid Date",
      align: "center",
      width: 110,
      render: (r) => (
        <span style={{ color: "#595959", fontSize: 13 }}>
          {r?.approvedAt
            ? moment(r.approvedAt).format("DD MMM YYYY")
            : moment(r?.date).format("DD MMM YYYY")}
        </span>
      ),
    },
    {
      title: "Status",
      align: "center",
      width: 100,
      render: (r) => statusTag(r?.status),
    },
    {
      title: "Actions",
      align: "center",
      width: 120,
      render: (r) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {r?.status === "pending" && canApprove && (
            <>
              <Popconfirm
                title="Approve this transaction?"
                onConfirm={() => handleApprove(r)}
                okText="Approve"
                cancelText="Cancel"
              >
                <Tooltip title="Approve">
                  <div style={{
                    width: 30, height: 30, borderRadius: 6, display: "flex",
                    alignItems: "center", justifyContent: "center", cursor: "pointer",
                    background: "#f6ffed", border: "1px solid #b7eb8f",
                  }}>
                    <CheckCircleOutlined style={{ fontSize: 16, color: "#52c41a" }} />
                  </div>
                </Tooltip>
              </Popconfirm>
              <Popconfirm
                title="Reject this transaction?"
                onConfirm={() => handleReject(r)}
                okText="Reject"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Reject">
                  <div style={{
                    width: 30, height: 30, borderRadius: 6, display: "flex",
                    alignItems: "center", justifyContent: "center", cursor: "pointer",
                    background: "#fff1f0", border: "1px solid #ffa39e",
                  }}>
                    <CloseCircleOutlined style={{ fontSize: 16, color: "#ff4d4f" }} />
                  </div>
                </Tooltip>
              </Popconfirm>
            </>
          )}
          <Tooltip title="View details">
            <div
              style={{
                width: 30, height: 30, borderRadius: 6, display: "flex",
                alignItems: "center", justifyContent: "center", cursor: "pointer",
                background: "#e6f7ff", border: "1px solid #91d5ff",
              }}
              onClick={() => {
                const breakdown = getTransactionBreakdown(r);
                setViewRecord({ ...r, breakdown });
                const s = r?.student;
                form.setFieldsValue({
                  fullName: s ? `${s.first_name || ""} ${s.last_name || ""}`.trim() : r?.name,
                  class: r?.class?.name,
                  feeType: getFeeTypeLabel(r),
                  totalFee: breakdown.total,
                  paid: breakdown.paid,
                  remaining: breakdown.remaining,
                  payment: r?.amount,
                  paymentType: r?.type,
                  date: moment(r?.date),
                });
              }}
            >
              <EyeOutlined style={{ fontSize: 16, color: "#1890ff" }} />
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Receipt",
      align: "center",
      width: 130,
      render: (r) =>
        r?.status === 'approved' ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
            <Tooltip title="Download receipt">
              <DownloadOutlined
                style={{ fontSize: 17, color: "#1890ff", cursor: "pointer" }}
                onClick={() => handleReceiptDownload(r)}
              />
            </Tooltip>
            <Tooltip title="Print receipt">
              <PrinterOutlined
                style={{ fontSize: 17, color: "#52c41a", cursor: "pointer" }}
                onClick={() => handleReceiptPrint(r)}
              />
            </Tooltip>
            {!r?.receipt_generated && (
              <Button
                size="small"
                style={{ borderRadius: 6, fontSize: 12 }}
                onClick={() => openReceiptModal(r)}
              >
                Generate Receipt
              </Button>
            )}
          </div>
        ) : null,
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: "24px 0" }}>
      <div style={{
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        overflow: "hidden",
      }}>

        {/* ── Page header ── */}
        <div style={{
          padding: "20px 28px 16px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a1a" }}>
              Transaction Approvals
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "#8c8c8c" }}>
              {tableData.length} transaction{tableData.length !== 1 ? "s" : ""} shown
            </p>
          </div>
          {statusTag(statusFilter === "all" ? null : statusFilter)}
        </div>

        {/* ── Filters ── */}
        <div style={{
          padding: "16px 28px",
          background: "#fafafa",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "flex-end",
        }}>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 100 }}>
            <span style={labelStyle}>Class</span>
            <Select
              showSearch
              allowClear
              placeholder="All classes"
              style={{ width: "100%" }}
              onChange={(v) => setClassId(v)}
            >
              {classes?.data?.map((d) => (
                <Option key={d._id} value={d._id}>{d.name}</Option>
              ))}
            </Select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", minWidth: 100 }}>
            <span style={labelStyle}>Academic Year</span>
            <Select value={selectedYear} onChange={(v) => setSelectedYear(v)} style={{ width: "100%" }}>
              {academicYears.map((yr) => (
                <Option key={yr} value={yr}>{yr}</Option>
              ))}
            </Select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", minWidth: 200 }}>
            <span style={labelStyle}>Student Name</span>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", minWidth: 200 }}>
            <span style={labelStyle}>Payment Date Range</span>
            <RangePicker
              format="DD MMM YYYY"
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [null, null])}
              allowClear
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", minWidth: 140 }}>
            <span style={labelStyle}>Status</span>
            <Select value={statusFilter} onChange={(v) => setStatusFilter(v)} style={{ width: "100%" }}>
              <Option value="all">All Statuses</Option>
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </div>
        </div>

        {/* ── Summary cards ── */}
        <div style={{ padding: "20px 28px 4px" }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Collection Summary
            </span>
            <span style={{
              fontSize: 12, color: "#595959",
              background: "#f5f5f5", border: "1px solid #e8e8e8",
              borderRadius: 20, padding: "2px 10px",
            }}>
              {dateRangeLabel}
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
            {/* Approved total */}
            <div style={{
              background: "linear-gradient(135deg, #f6ffed, #d9f7be)",
              border: "1px solid #b7eb8f", borderRadius: 10,
              padding: "16px 18px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#389e0d", marginBottom: 6 }}>
                Total Approved
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#237804", lineHeight: 1.2 }}>
                {fmt(sum(approvedTx))}
              </div>
              <div style={{ fontSize: 12, color: "#52c41a", marginTop: 4 }}>
                {approvedTx.length} transaction{approvedTx.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Cash to hand over */}
            {/* <div style={{
              background: "linear-gradient(135deg, #e6f7ff, #bae7ff)",
              border: "1px solid #91d5ff", borderRadius: 10,
              padding: "16px 18px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#096dd9", marginBottom: 6 }}>
                Cash to Hand Over
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0050b3", lineHeight: 1.2 }}>
                {fmt(sum(cashApproved))}
              </div>
              <div style={{ fontSize: 12, color: "#1890ff", marginTop: 4 }}>
                {cashApproved.length} cash transaction{cashApproved.length !== 1 ? "s" : ""}
              </div>
            </div> */}

            {/* Pending approval */}
            <div style={{
              background: "linear-gradient(135deg, #fff7e6, #ffe7ba)",
              border: "1px solid #ffd591", borderRadius: 10,
              padding: "16px 18px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#d46b08", marginBottom: 6 }}>
                Awaiting Approval
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#ad4e00", lineHeight: 1.2 }}>
                {fmt(sum(pendingTx))}
              </div>
              <div style={{ fontSize: 12, color: "#fa8c16", marginTop: 4 }}>
                {pendingTx.length} transaction{pendingTx.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Cash pending approval */}
            {/* <div style={{
              background: "linear-gradient(135deg, #fff1f0, #ffccc7)",
              border: "1px solid #ffa39e", borderRadius: 10,
              padding: "16px 18px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#cf1322", marginBottom: 6 }}>
                Cash Pending
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#a8071a", lineHeight: 1.2 }}>
                {fmt(sum(cashPending))}
              </div>
              <div style={{ fontSize: 12, color: "#ff4d4f", marginTop: 4 }}>
                {cashPending.length} cash transaction{cashPending.length !== 1 ? "s" : ""}
              </div>
            </div> */}
          </div>
        </div>

        {/* ── Bulk action bar ── */}
        {selectedRowKeys.length > 0 && (
          <div style={{
            margin: "12px 28px",
            padding: "10px 16px",
            borderRadius: 8,
            background: "linear-gradient(90deg, #e6f7ff, #f0f5ff)",
            border: "1px solid #91d5ff",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "nowrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 4 }}>
              <span style={{
                background: "#1890ff", color: "#fff", borderRadius: 20,
                padding: "1px 10px", fontSize: 12, fontWeight: 700,
                display: "inline-flex", alignItems: "center", height: 20,
              }}>
                {selectedRowKeys.length}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#003a8c", display: "inline-flex", alignItems: "center" }}>
                selected &nbsp;·&nbsp; {pendingSelected.length} pending
              </span>
            </div>
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              loading={bulkApproveLoading}
              disabled={pendingSelected.length === 0 || !canApprove}
              onClick={handleBulkApprove}
              style={{ borderRadius: 6, display: "inline-flex", alignItems: "center" }}
            >
              Approve {pendingSelected.length > 0 ? `(${pendingSelected.length})` : ""}
            </Button>
            <Popconfirm
              title={`Reject ${pendingSelected.length} transaction(s)?`}
              onConfirm={handleBulkReject}
              okText="Reject"
              okButtonProps={{ danger: true }}
              disabled={pendingSelected.length === 0 || !canApprove}
            >
              <Button
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                loading={bulkRejectLoading}
                disabled={pendingSelected.length === 0 || !canApprove}
                style={{ borderRadius: 6, display: "inline-flex", alignItems: "center" }}
              >
                Reject {pendingSelected.length > 0 ? `(${pendingSelected.length})` : ""}
              </Button>
            </Popconfirm>
            <Button
              size="small"
              onClick={() => setSelectedRowKeys([])}
              style={{ borderRadius: 6, marginLeft: "auto", display: "inline-flex", alignItems: "center" }}
            >
              Clear selection
            </Button>
          </div>
        )}

        {/* ── Table ── */}
        <div style={{ padding: "0 28px 24px" }}>
          <Table
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
              getCheckboxProps: (r) => ({ disabled: r?.status !== "pending" }),
            }}
            rowKey="_id"
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: 15,
              showSizeChanger: false,
              showTotal: (total) => `${total} transactions`,
              style: { marginTop: 16 },
            }}
            loading={transactionApproveData?.isLoading}
            size="middle"
            rowClassName={(r) =>
              r?.status === "pending" ? "transaction-row-pending" : ""
            }
          />
        </div>

        {/* ── Generate Receipt modal ── */}
        <Modal
          title="Generate Receipt"
          visible={receiptModal.visible}
          onCancel={() => setReceiptModal({ visible: false, record: null })}
          onOk={handleGenerateReceipt}
          okText="Generate"
          confirmLoading={receiptGenerating}
          width={380}
          style={{ top: 80 }}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 2 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  Student
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>
                  {(() => {
                    const s = receiptModal.record?.student;
                    return s ? `${s.first_name || ""} ${s.last_name || ""}`.trim() : receiptModal.record?.name || "—";
                  })()}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  Amount Paid
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#237804" }}>
                  ₹ {(receiptModal.record?.amount || 0).toLocaleString("en-IN")}
                </div>
              </div>
            </div>
          </div>
        </Modal>

        {/* ── View / Approve modal ── */}
        <Modal
          width={420}
          visible={!!viewRecord}
          onCancel={() => { setViewRecord(null); form.resetFields(); }}
          footer={null}
          bodyStyle={{ padding: 0 }}
          style={{ top: 60 }}
        >
          {/* Modal header */}
          <div style={{
            padding: "20px 24px 16px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Transaction
              </p>
              <h3 style={{ margin: "2px 0 4px", fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>
                {viewRecord?.unique_id || "--"}
              </h3>
              {statusTag(viewRecord?.status)}
            </div>
            <div style={{ textAlign: "right", fontSize: 13, color: "#8c8c8c" }}>
              <div>{moment(viewRecord?.date).format("DD MMM YYYY")}</div>
              <div style={{ textTransform: "capitalize", marginTop: 2 }}>{viewRecord?.type}</div>
            </div>
          </div>

          <div style={{ padding: "20px 24px" }}>
            <Form layout="vertical" form={form}>

              {/* Student info */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <Form.Item label="Student" name="fullName" style={{ flex: 2, marginBottom: 0 }}>
                  <Input readOnly style={{ background: "#fafafa" }} />
                </Form.Item>
                <Form.Item label="Class" name="class" style={{ flex: 1, marginBottom: 0 }}>
                  <Input readOnly style={{ background: "#fafafa" }} />
                </Form.Item>
              </div>

              <Form.Item label="Fee Type" name="feeType" style={{ marginBottom: 16 }}>
                <Input readOnly style={{ background: "#fafafa" }} />
              </Form.Item>

              {/* Fee summary cards */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10, marginBottom: 16,
              }}>
                {[
                  { label: "Total Fee", field: "totalFee", color: "#595959" },
                  { label: "Total Paid", field: "paid",     color: "#389e0d" },
                  { label: "Remaining",  field: "remaining", color: "#d46b08" },
                ].map(({ label, field, color }) => (
                  <div key={field} style={{
                    background: "#fafafa", border: "1px solid #f0f0f0",
                    borderRadius: 8, padding: "10px 12px", textAlign: "center",
                  }}>
                    <div style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color }}>
                      ₹ {(form.getFieldValue(field) || 0).toLocaleString("en-IN")}
                    </div>
                  </div>
                ))}
              </div>

              {/* Payment highlight */}
              <div style={{
                background: "linear-gradient(135deg, #e6f7ff, #f0f5ff)",
                border: "1px solid #91d5ff", borderRadius: 8,
                padding: "12px 16px", marginBottom: 20,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#003a8c" }}>
                  This Payment
                </span>
                <span style={{ fontSize: 20, fontWeight: 800, color: "#1890ff" }}>
                  ₹ {(viewRecord?.amount || 0).toLocaleString("en-IN")}
                </span>
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                {viewRecord?.status === "pending" && canApprove && (
                  <>
                    <Button
                      type="primary"
                      style={{ borderRadius: 6, flex: 1 }}
                      onClick={async () => {
                        await handleApprove(viewRecord);
                        setViewRecord(null);
                        form.resetFields();
                      }}
                    >
                      Approve
                    </Button>
                    <Popconfirm
                      title="Reject this transaction?"
                      onConfirm={async () => {
                        await handleReject(viewRecord);
                        setViewRecord(null);
                        form.resetFields();
                      }}
                      okText="Reject"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        danger
                        style={{ borderRadius: 6, flex: 1 }}
                      >
                        Reject
                      </Button>
                    </Popconfirm>
                  </>
                )}
                <Button
                  onClick={() => { setViewRecord(null); form.resetFields(); }}
                  style={{ borderRadius: 6 }}
                >
                  Close
                </Button>
              </div>
            </Form>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ApproveTransaction;
